import { ROUTES } from "src/utils/constants/routes";
import { isEmpty } from "@groww-tech-pvt/ella";
import { AOF_STATUS, DATA_TYPE, JOURNEY_TYPES, ONBOARDING_FLOW_SCREENS, SEARCH_PARAMS } from "core/constants";
import { useCosmos } from "core/hooks/useCosmos";
import useOnboardingStore from "core/store";
import { useCallback, useEffect, useRef } from "react";
import { getSearchParams } from "core/hooks/useGetParam";
import { useNavigateToRoute } from "core/hooks/useOnboardingRouter";
import { getDigilockerParams } from "core/utils/digilockerParamCache";
import { useUserDataStore } from "src/utils/store/UserData";

export const useCosmosController = () => {

  const { activeScreen, currentDataType, setActiveScreen, isOnbDataLoading } = useOnboardingStore();
  const { userData } = useUserDataStore();
  const isUserStockOnboarded = userData?.aof_status === AOF_STATUS.COMPLETED;
  const { navigateToRoute } = useNavigateToRoute();
  const initiateRef = useRef(false);

  const { journeyType, digio_doc_id, status, success } = getSearchParams();

  const handleCosmosSuccess = useCallback(() => {
    setActiveScreen(ONBOARDING_FLOW_SCREENS.ONBOARDING_PENDING_SCREEN);
  }, [setActiveScreen]);

  const { initiateCosmosFlow } = useCosmos({ handleCosmosSuccess, journeyType: (journeyType as string) });
  const digiStatus = status || success;

  const isUserOnboarded = isUserStockOnboarded && (isEmpty(journeyType) || journeyType === JOURNEY_TYPES.MAIN_ONBOARDING);

  // TODO: drop this wait once /onboarding/nri route is removed
  // journeyType/digio_doc_id arrive a tick late after redirect, so wait for
  // one of them — unless already onboarded, which needs neither.
  const hasRedirectParams = !isEmpty(journeyType) || Boolean(digio_doc_id);
  const canInitiate = isUserOnboarded || hasRedirectParams;

  useEffect(() => {
    if (initiateRef.current || !canInitiate) return;
    initiateRef.current = true;
    redirectUserToCorrectScreen();
  }, [canInitiate])

  const redirectUserToCorrectScreen = () => {
    if (isUserOnboarded) {
      setActiveScreen(ONBOARDING_FLOW_SCREENS.SUCCESS_SCREEN);
    } else {
      performActionBasisDataTypeOnRedirection();
    }
  };

  const performActionBasisDataTypeOnRedirection = () => {
    const cached = getDigilockerParams();
    const digilockerParamPresent = !isEmpty(cached);

    if (digilockerParamPresent) {
      navigateToRoute(ROUTES.ONBOARDING, {
        journeyType: cached?.[SEARCH_PARAMS.JOURNEY_TYPES] ?? journeyType ?? JOURNEY_TYPES.MAIN_ONBOARDING,
        dataType: cached?.[SEARCH_PARAMS.DATA_TYPE] ?? DATA_TYPE.PROOF_OF_ADDRESS_FRONT,
        status: digiStatus,
        digio_doc_id: digio_doc_id,
      });
      if (cached?.[SEARCH_PARAMS.DATA_TYPE] === DATA_TYPE.PROOF_OF_ADDRESS_FRONT) {
        setActiveScreen(ONBOARDING_FLOW_SCREENS.DIGILOCKER);
      } else if (cached?.[SEARCH_PARAMS.DATA_TYPE] === DATA_TYPE.ACCOUNT_FORM) {
        setActiveScreen(ONBOARDING_FLOW_SCREENS.ESIGN);
      }
      return;
    } else {
      initiateCosmosFlow();
    }
  }


  // Handle screen navigation when activeScreen changes
  useEffect(() => {

    if (activeScreen && !digio_doc_id && !isEmpty(journeyType)) {

      // TODO :: this will be removed once we have a entire flow for NRI onboarding,
      // currently we are using different route for NRI onboarding `/onboarding/nri` 
      const basePath = journeyType === JOURNEY_TYPES.NRI_ONBOARDING_V2
        ? ROUTES.NRI_ONBOARDING
        : ROUTES.ONBOARDING;

      navigateToRoute(basePath, {
        journeyType: journeyType,
        dataType: currentDataType ?? null,
      });
    }
  }, [activeScreen, currentDataType]);

  return {
    activeScreen,
    isOnbDataLoading
  }
}
